import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Calculator, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate, errMsg } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge, Pager } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// ---------- agent rate calculator (final prices only — base rates are admin-only) ----------
function AgentRateCalculator() {
  const [form, setForm] = useState({ delivery_pincode: "", cod: true });
  const [pkgType, setPkgType] = useState("small");
  const [weight, setWeight] = useState("");
  const [dims, setDims] = useState({ length: "", width: "", height: "" });
  const [boxes, setBoxes] = useState([{ weight: "", length: "", width: "", height: "" }]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const calc = async () => {
    setBusy(true);
    setResult(null);
    try {
      const payload = { delivery_pincode: form.delivery_pincode, cod: form.cod, package_type: pkgType };
      if (pkgType === "heavy") {
        payload.boxes = boxes.map((b) => ({ weight: parseFloat(b.weight) || 0, length: parseFloat(b.length) || 0, width: parseFloat(b.width) || 0, height: parseFloat(b.height) || 0 }));
      } else {
        payload.weight = parseFloat(weight) || 0.5;
        payload.length = parseFloat(dims.length) || 0;
        payload.width = parseFloat(dims.width) || 0;
        payload.height = parseFloat(dims.height) || 0;
      }
      const { data } = await api.post("/shipping/calculate-rates", payload);
      setResult(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setBox = (i, k, v) => setBoxes(boxes.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const canCalc = pkgType === "small" ? !!weight : boxes.every((b) => parseFloat(b.weight) > 0);
  const inp = "border-slate-700 bg-slate-900 text-slate-100 text-xs";
  return (
    <div className="mb-5 rounded-xl border border-slate-800 bg-[#111827] p-4" data-testid="agent-rate-calculator">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-100"><Calculator size={14} className="text-orange-400" /> Shipping rate calculator</h3>
      <div className="mb-3 grid max-w-md grid-cols-2 gap-2" data-testid="agent-package-type">
        {[["small", "Small < 10 KG · single box"], ["heavy", "Heavy ≥ 10 KG · multi-box"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`agent-pkg-${v}`} onClick={() => setPkgType(v)}
            className={`rounded-lg border px-3 py-2 text-[11px] font-bold transition-colors ${pkgType === v ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400 hover:border-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Delivery pincode</p>
          <Input data-testid="agent-calc-pincode" value={form.delivery_pincode} onChange={(e) => setForm({ ...form, delivery_pincode: e.target.value })} placeholder="560001" className={`w-28 ${inp}`} />
        </div>
        {pkgType === "small" ? (
          <>
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Weight (KG)</p>
              <Input data-testid="agent-calc-weight" type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0.5" className={`w-24 ${inp}`} />
            </div>
            {[["length", "L"], ["width", "W"], ["height", "H"]].map(([k, l]) => (
              <div key={k}>
                <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{l} cm</p>
                <Input data-testid={`agent-calc-${k}`} type="number" value={dims[k]} onChange={(e) => setDims({ ...dims, [k]: e.target.value })} className={`w-20 ${inp}`} />
              </div>
            ))}
          </>
        ) : (
          <div className="w-full space-y-2" data-testid="agent-calc-boxes">
            {boxes.map((b, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5" data-testid={`agent-box-${i}`}>
                <span className="text-[10px] font-bold uppercase text-slate-500">Box {i + 1}</span>
                <Input data-testid={`agent-box-${i}-weight`} type="number" step="0.1" value={b.weight} onChange={(e) => setBox(i, "weight", e.target.value)} placeholder="KG *" className={`w-20 ${inp}`} />
                <Input data-testid={`agent-box-${i}-length`} type="number" value={b.length} onChange={(e) => setBox(i, "length", e.target.value)} placeholder="L" className={`w-16 ${inp}`} />
                <Input data-testid={`agent-box-${i}-width`} type="number" value={b.width} onChange={(e) => setBox(i, "width", e.target.value)} placeholder="W" className={`w-16 ${inp}`} />
                <Input data-testid={`agent-box-${i}-height`} type="number" value={b.height} onChange={(e) => setBox(i, "height", e.target.value)} placeholder="H" className={`w-16 ${inp}`} />
                {boxes.length > 1 && (
                  <button data-testid={`agent-box-del-${i}`} onClick={() => setBoxes(boxes.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 size={12} /></button>
                )}
              </div>
            ))}
            <button type="button" data-testid="agent-box-add" onClick={() => setBoxes([...boxes, { weight: "", length: "", width: "", height: "" }])}
              className="flex items-center gap-1 text-[11px] font-semibold text-orange-400 hover:text-orange-300"><Plus size={11} /> Add box</button>
          </div>
        )}
        <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-300">
          <input data-testid="agent-calc-cod" type="checkbox" checked={form.cod} onChange={(e) => setForm({ ...form, cod: e.target.checked })} /> COD
        </label>
        <Button data-testid="agent-calc-submit" size="sm" onClick={calc} disabled={busy || !canCalc}
          className="bg-orange-500 font-semibold text-white hover:bg-orange-600">{busy ? "…" : "Get rates"}</Button>
      </div>
      {result && (
        <div className="mt-3" data-testid="agent-calc-results">
          <p className="mb-2 text-[10px] text-slate-500">
            Chargeable weight <b className="text-orange-400">{result.weights.chargeable} KG</b> (actual {result.weights.actual} / volumetric {result.weights.volumetric}) · {result.package_type} shipment
            {result.mode === "demo" && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 font-bold text-amber-400">DEMO RATES</span>}
          </p>
          {!result.services.length ? (
            <p className="text-xs text-rose-300">No courier can handle {result.weights.chargeable} KG on this route.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {result.services.map((s) => (
                <div key={s.service} data-testid={`agent-calc-${s.service}`} className="rounded-lg border border-slate-800 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-200">{s.name}</p>
                    <p className="text-sm font-extrabold text-orange-400">{fmtINR(s.final_with_cod)}</p>
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-500">{s.chargeable_weight} KG · EDD {s.eta_days} days{s.cod_fee ? " · incl. COD fee" : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentShipments() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [tracking, setTracking] = useState(null);

  const { data } = useQuery({
    queryKey: ["agent-shipments", page],
    queryFn: async () => (await api.get(`/shipping/shipments?page=${page}&limit=15`)).data,
  });

  const track = useMutation({
    mutationFn: async (awb) => (await api.get(`/shipping/track/${awb}`)).data,
    onSuccess: (r, awb) => { setTracking({ awb, ...r }); qc.invalidateQueries({ queryKey: ["agent-shipments"] }); },
  });

  return (
    <div data-testid="agent-shipments-page">
      <PageHeader title="Shipments" sub="IndiaFast AWB tracking for your orders." testid="shipments-title" />

      <AgentRateCalculator />

      {!data?.items?.length ? <EmptyState title="No shipments yet" sub="Ship an order from the Orders page." /> : (
        <div className="space-y-2">
          {data.items.map((s) => (
            <div key={s.id} data-testid={`shipment-${s.awb}`} className="rounded-xl border border-slate-800 bg-[#111827] px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm text-slate-100">{s.awb}</p>
                    <StatusBadge status={s.status} />
                  </div>
                  <p className="mt-1 text-slate-400">{s.order_no} · {s.customer?.name} · {s.customer?.city}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">IndiaFast {s.service} · {fmtINR(s.cost)} · {fmtDate(s.created_at)}</p>
                </div>
                <Button data-testid={`track-btn-${s.awb}`} size="sm" variant="outline" onClick={() => track.mutate(s.awb)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"><RefreshCw size={12} /> Track</Button>
              </div>
              {tracking?.awb === s.awb && (
                <div data-testid={`tracking-timeline-${s.awb}`} className="mt-3 space-y-1.5 border-t border-slate-800 pt-3">
                  {(tracking.events || []).map((e, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-xs">
                      <span className={`h-2 w-2 rounded-full ${i === tracking.events.length - 1 ? "bg-orange-500" : "bg-slate-600"}`} />
                      <span className="text-slate-300">{e.label}</span>
                      <span className="font-mono text-[10px] text-slate-500">{fmtDate(e.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}
    </div>
  );
}
